import { type FormEvent, useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { createClient, getClient, updateClient } from '../../api/clients'
import { Button, ButtonLink } from '../../components/ui/Button'
import { Card, ErrorBox, SectionTitle } from '../../components/ui/Card'
import { FormField, inputClass, textareaClass } from '../../components/ui/FormField'
import { useToast } from '../../components/ui/Toast'
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges'
import type { ClientCreate } from '../../types/clients'

interface FieldErrors {
  first_name?: string
  last_name?: string
  email?: string
  phone?: string
  phone_alt?: string
  notes?: string
}

function isValidEmail(v: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)
}

function isValidPhone(v: string): boolean {
  const cleaned = v.replace(/[\s\-().+]/g, '')
  return cleaned.length >= 7 && cleaned.length <= 30
}

type FormState = {
  first_name: string; last_name: string; email: string; phone: string
  phone_alt: string; address_line1: string; address_line2: string
  city: string; state: string; postal_code: string; country: string; notes: string
  reminders_opt_out: boolean
}

const EMPTY: FormState = {
  first_name: '', last_name: '', email: '', phone: '',
  phone_alt: '', address_line1: '', address_line2: '',
  city: '', state: '', postal_code: '', country: '', notes: '',
  reminders_opt_out: false,
}

export default function ClientForm() {
  const { id } = useParams<{ id: string }>()
  const isEdit = Boolean(id)
  const navigate = useNavigate()

  const toast = useToast()
  const [form, setForm] = useState<FormState>(EMPTY)
  const [isDirty, setIsDirty] = useState(false)
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({})
  const [apiError, setApiError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [loading, setLoading] = useState(isEdit)

  useUnsavedChanges(isDirty && !submitting)

  useEffect(() => {
    if (!isEdit || !id) return
    const load = async () => {
      try {
        const client = await getClient(Number(id))
        setForm({
          first_name: client.first_name, last_name: client.last_name,
          email: client.email, phone: client.phone,
          phone_alt: client.phone_alt ?? '', address_line1: client.address_line1 ?? '',
          address_line2: client.address_line2 ?? '', city: client.city ?? '',
          state: client.state ?? '', postal_code: client.postal_code ?? '',
          country: client.country ?? '', notes: client.notes ?? '',
          reminders_opt_out: client.reminders_opt_out,
        })
      } catch {
        setLoadError('Client not found or could not be loaded.')
      } finally {
        setLoading(false)
      }
    }
    void load()
  }, [id, isEdit])

  const set = (field: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm((f) => ({ ...f, [field]: e.target.value }))
    setIsDirty(true)
  }

  const validateField = (field: keyof FieldErrors, value: string): string | undefined => {
    switch (field) {
      case 'first_name': return value.trim().length === 0 ? 'First name is required' : value.trim().length > 100 ? 'Max 100 characters' : undefined
      case 'last_name':  return value.trim().length === 0 ? 'Last name is required'  : value.trim().length > 100 ? 'Max 100 characters' : undefined
      case 'email':      return value.trim().length === 0 ? 'Email is required' : !isValidEmail(value.trim()) ? 'Enter a valid email address' : undefined
      case 'phone':      return value.trim().length === 0 ? 'Phone number is required' : !isValidPhone(value) ? 'Enter a valid phone number (at least 7 digits)' : undefined
      case 'phone_alt':  return (value.trim().length > 0 && !isValidPhone(value)) ? 'Enter a valid phone number (at least 7 digits)' : undefined
      case 'notes':      return value.length > 2000 ? 'Max 2000 characters' : undefined
      default: return undefined
    }
  }

  const handleBlur = (field: keyof FieldErrors, value: string) => {
    const err = validateField(field, value)
    setFieldErrors((prev) => { const next = { ...prev }; if (err) next[field] = err; else delete next[field]; return next })
  }

  const validateAll = (): boolean => {
    const fields: (keyof FieldErrors)[] = ['first_name', 'last_name', 'email', 'phone', 'phone_alt', 'notes']
    const errors: FieldErrors = {}
    for (const field of fields) { const err = validateField(field, form[field]); if (err) errors[field] = err }
    setFieldErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (submitting) return
    setApiError(null)
    if (!validateAll()) return
    setSubmitting(true)
    const payload: ClientCreate = {
      first_name: form.first_name.trim(), last_name: form.last_name.trim(),
      email: form.email.trim(), phone: form.phone.trim(),
      phone_alt: form.phone_alt.trim() || undefined,
      address_line1: form.address_line1.trim() || undefined, address_line2: form.address_line2.trim() || undefined,
      city: form.city.trim() || undefined, state: form.state.trim() || undefined,
      postal_code: form.postal_code.trim() || undefined, country: form.country.trim() || undefined,
      notes: form.notes.trim() || undefined,
      reminders_opt_out: form.reminders_opt_out,
    }
    try {
      if (isEdit && id) {
        await updateClient(Number(id), payload)
        setIsDirty(false)
        toast.success('Client updated successfully.')
        void navigate(`/clients/${id}`)
      } else {
        const created = await createClient(payload)
        setIsDirty(false)
        toast.success('Client created successfully.')
        void navigate(`/clients/${created.id}`)
      }
    } catch (err) {
      setApiError(err instanceof Error ? err.message : 'Something went wrong. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) return <div className="text-text-2 py-8">Loading…</div>

  if (loadError) {
    return (
      <ErrorBox>{loadError} <Link to="/clients" className="text-accent no-underline font-medium">Back to clients</Link></ErrorBox>
    )
  }

  const cancelTarget = isEdit && id ? `/clients/${id}` : '/clients'

  return (
    <div>
      <nav className="text-[0.85rem] text-text-3 mb-5 flex items-center gap-[0.4rem]">
        <Link to="/clients" className="text-accent no-underline font-medium">Clients</Link>
        <span>›</span>
        <span>{isEdit ? `Edit ${form.first_name} ${form.last_name}`.trim() || 'Edit Client' : 'New Client'}</span>
      </nav>

      <h1 className="text-[1.4rem] font-bold text-text-heading mt-0 mb-7">
        {isEdit ? 'Edit Client' : 'New Client'}
      </h1>

      <Card className="max-w-[680px]">
        {apiError && <ErrorBox className="mb-5">{apiError}</ErrorBox>}

        <form onSubmit={(e) => void handleSubmit(e)} noValidate>
          {/* Contact Information */}
          <div className="mb-8">
            <SectionTitle>Contact Information</SectionTitle>
            <div className="grid grid-cols-2 gap-x-4 gap-y-0 max-sm:grid-cols-1">
              <FormField label="First name" htmlFor="first_name" required error={fieldErrors.first_name}>
                <input id="first_name" className={inputClass(!!fieldErrors.first_name)} type="text" value={form.first_name}
                  onChange={set('first_name')} onBlur={(e) => handleBlur('first_name', e.target.value)}
                  autoComplete="given-name" disabled={submitting} maxLength={100} />
              </FormField>
              <FormField label="Last name" htmlFor="last_name" required error={fieldErrors.last_name}>
                <input id="last_name" className={inputClass(!!fieldErrors.last_name)} type="text" value={form.last_name}
                  onChange={set('last_name')} onBlur={(e) => handleBlur('last_name', e.target.value)}
                  autoComplete="family-name" disabled={submitting} maxLength={100} />
              </FormField>
            </div>

            <FormField label="Email address" htmlFor="email" required error={fieldErrors.email}>
              <input id="email" className={inputClass(!!fieldErrors.email)} type="email" value={form.email}
                onChange={set('email')} onBlur={(e) => handleBlur('email', e.target.value)}
                autoComplete="email" disabled={submitting} maxLength={255} />
            </FormField>

            <div className="grid grid-cols-2 gap-x-4 gap-y-0 max-sm:grid-cols-1">
              <FormField label="Phone" htmlFor="phone" required error={fieldErrors.phone}>
                <input id="phone" className={inputClass(!!fieldErrors.phone)} type="tel" value={form.phone}
                  onChange={set('phone')} onBlur={(e) => handleBlur('phone', e.target.value)}
                  autoComplete="tel" disabled={submitting} maxLength={30} />
              </FormField>
              <FormField label="Phone (alternate)" htmlFor="phone_alt" error={fieldErrors.phone_alt}>
                <input id="phone_alt" className={inputClass(!!fieldErrors.phone_alt)} type="tel" value={form.phone_alt}
                  onChange={set('phone_alt')} onBlur={(e) => handleBlur('phone_alt', e.target.value)}
                  autoComplete="tel" disabled={submitting} maxLength={30} />
              </FormField>
            </div>
          </div>

          {/* Address */}
          <div className="mb-8">
            <SectionTitle>Address</SectionTitle>
            <FormField label="Address line 1" htmlFor="address_line1">
              <input id="address_line1" className={inputClass()} type="text" value={form.address_line1}
                onChange={set('address_line1')} autoComplete="address-line1" disabled={submitting} maxLength={200} />
            </FormField>
            <FormField label="Address line 2" htmlFor="address_line2">
              <input id="address_line2" className={inputClass()} type="text" value={form.address_line2}
                onChange={set('address_line2')} autoComplete="address-line2" disabled={submitting} maxLength={200} />
            </FormField>
            <div className="grid grid-cols-3 gap-x-4 gap-y-0 max-sm:grid-cols-1">
              <FormField label="City" htmlFor="city">
                <input id="city" className={inputClass()} type="text" value={form.city}
                  onChange={set('city')} autoComplete="address-level2" disabled={submitting} maxLength={100} />
              </FormField>
              <FormField label="State / Province" htmlFor="state">
                <input id="state" className={inputClass()} type="text" value={form.state}
                  onChange={set('state')} autoComplete="address-level1" disabled={submitting} maxLength={100} />
              </FormField>
              <FormField label="Postal code" htmlFor="postal_code">
                <input id="postal_code" className={inputClass()} type="text" value={form.postal_code}
                  onChange={set('postal_code')} autoComplete="postal-code" disabled={submitting} maxLength={20} />
              </FormField>
            </div>
            <FormField label="Country" htmlFor="country" className="max-w-[50%]">
              <input id="country" className={inputClass()} type="text" value={form.country}
                onChange={set('country')} autoComplete="country-name" disabled={submitting} maxLength={100} />
            </FormField>
          </div>

          {/* Notes */}
          <div className="mb-8">
            <SectionTitle>Notes</SectionTitle>
            <FormField label="Notes" htmlFor="notes" error={fieldErrors.notes} hint={`${form.notes.length}/2000`}>
              <textarea id="notes" className={textareaClass(!!fieldErrors.notes)} value={form.notes}
                onChange={set('notes')} onBlur={(e) => handleBlur('notes', e.target.value)}
                disabled={submitting} maxLength={2000} placeholder="Any relevant notes about this client…" />
            </FormField>
            <FormField label="Reminders">
              <label className="flex items-center gap-2 cursor-pointer text-[0.9rem]">
                <input
                  type="checkbox" checked={!form.reminders_opt_out} disabled={submitting}
                  onChange={(e) => { setForm((f) => ({ ...f, reminders_opt_out: !e.target.checked })); setIsDirty(true) }}
                />
                Send vaccination and appointment reminder emails
              </label>
            </FormField>
          </div>

          <div className="flex gap-3 pt-6 border-t border-border">
            <Button type="submit" size="lg" disabled={submitting}>
              {submitting ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Client'}
            </Button>
            <ButtonLink to={cancelTarget} variant="outline" size="lg">Cancel</ButtonLink>
          </div>
        </form>
      </Card>
    </div>
  )
}
